import { useState } from "react";
import { toBlob } from "html-to-image";
import { ImageDown, Download, Copy, Share2, Linkedin, Check, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { localApi } from "../lib/localApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const LOGO = "/casaneo-logo-wide.webp";

/* Rendu hors écran : tableau cloné + pied Casanéo, puis PNG (fond sombre, 1080px). */
async function renderTablePng(tableEl, { title, pageUrl }) {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-20000px;top:0;";
  const card = document.createElement("div");
  card.style.cssText = "width:1080px;padding:40px 44px 32px;background:#071e42;color:#e2e8f0;font-family:system-ui,-apple-system,'Segoe UI',Arial,sans-serif;";
  host.appendChild(card);
  const head = document.createElement("div");
  head.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:24px;margin-bottom:22px;";
  head.innerHTML = `<div style="font-size:22px;font-weight:600;line-height:1.25;color:#fff;letter-spacing:-0.01em">${title}</div><img src="${LOGO}" alt="Casanéo" style="height:40px;flex-shrink:0" />`;
  const clone = tableEl.cloneNode(true);
  clone.querySelectorAll("[data-no-export]").forEach((n) => n.remove());
  clone.style.cssText = "border:1px solid rgba(255,255,255,0.12);border-radius:14px;overflow:visible;";
  clone.querySelectorAll("table").forEach((t) => { t.style.width = "100%"; t.style.fontSize = "17px"; t.style.minWidth = "0"; t.style.height = "auto"; t.style.fontFamily = "inherit"; });
  clone.querySelectorAll("th, td, tr").forEach((c) => { c.style.padding = "12px 16px"; c.style.height = "auto"; c.style.fontFamily = "inherit"; });
  clone.querySelectorAll("thead th:first-child").forEach((c) => { c.style.borderTopLeftRadius = "13px"; });
  clone.querySelectorAll("thead th:last-child").forEach((c) => { c.style.borderTopRightRadius = "13px"; });
  const foot = document.createElement("div");
  foot.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-top:20px;font-size:14px;color:#94a3b8;";
  foot.innerHTML = `<span>Source : <span style="color:#e7c873">${pageUrl.replace(/^https?:\/\//, "")}</span></span><span>Logiciel de conciergerie · casaneo.pro</span>`;
  card.append(head, clone, foot);
  document.body.appendChild(host);
  try {
    await Promise.all([...card.querySelectorAll("img")].map((im) => im.complete ? null : new Promise((r) => { im.onload = im.onerror = r; })));
    await document.fonts?.ready;
    const opts = { pixelRatio: 2, backgroundColor: "#071e42", cacheBust: true, skipFonts: true };
    return await toBlob(card, opts);
  } finally { host.remove(); }
}

export function TableShareButton({ tableRef, title, pageUrl, testId = "table-share" }) {
  const [busy, setBusy] = useState(false);
  const [img, setImg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [post, setPost] = useState("");
  const [postState, setPostState] = useState("idle");
  const [postCopied, setPostCopied] = useState(false);
  const fileName = `casaneo-comparatif-${(title || "tableau").toLowerCase().normalize("NFD").replace(/[^a-z0-9]+/g, "-").slice(0, 60)}.png`;
  const canCopy = typeof ClipboardItem !== "undefined" && !!navigator.clipboard?.write;

  const fetchPost = async (regen = false) => {
    setPostState("loading");
    try {
      const rows = [...tableRef.current.querySelectorAll("tr")].map((tr) => [...tr.querySelectorAll("th,td")].map((c) => c.textContent.trim()));
      const { data } = await localApi.post("/public/share/table-post", { title, page_url: pageUrl, head: rows[0] || [], rows: rows.slice(1), regen });
      setPost(data.text || ""); setPostState("ok");
    } catch { setPost(`${title}\n\n${pageUrl}`); setPostState("error"); }
  };
  const generate = async () => {
    if (!tableRef.current) return;
    setBusy(true);
    fetchPost(false);
    try {
      const blob = await renderTablePng(tableRef.current, { title, pageUrl });
      if (!blob) throw new Error("empty");
      setImg({ blob, url: URL.createObjectURL(blob) });
    } catch { toast.error("Impossible de générer l'image — réessayez"); }
    finally { setBusy(false); }
  };
  const close = () => { if (img) URL.revokeObjectURL(img.url); setImg(null); setCopied(false); setPostCopied(false); };
  const download = () => { const a = document.createElement("a"); a.href = img.url; a.download = fileName; a.click(); };
  const copy = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": img.blob, "text/plain": new Blob([post], { type: "text/plain" }) })]);
      setCopied(true); toast.success("Image + texte copiés — collez dans votre post (Ctrl+V)"); setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Copie impossible sur ce navigateur — téléchargez l'image"); }
  };
  const copyPost = async () => {
    try { await navigator.clipboard.writeText(post); setPostCopied(true); toast.success("Texte du post copié"); setTimeout(() => setPostCopied(false), 2500); }
    catch { toast.error("Copie impossible"); }
  };
  const share = async () => {
    const file = new File([img.blob], fileName, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title, text: post || `${title} — ${pageUrl}` }); } catch { /* annulé */ } }
    else { download(); toast.message("Image téléchargée — partagez-la depuis votre galerie ou WhatsApp"); }
  };
  const linkedin = async () => {
    if (canCopy) await copy();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`, "_blank", "noopener");
  };

  const btn = "inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 hover:bg-white/10 hover:text-white transition-colors";
  return (
    <>
      <button type="button" onClick={generate} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-60" data-testid={`${testId}-open`} data-no-export>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageDown className="h-3.5 w-3.5" />} {busy ? "Génération…" : "Partager ce tableau en image"}
      </button>
      <Dialog open={!!img} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0b1a35] border-white/15 text-slate-100" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="text-white">Tableau prêt à partager</DialogTitle></DialogHeader>
          {img && <img src={img.url} alt={title} className="w-full rounded-xl border border-white/10" data-testid={`${testId}-preview`} />}
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`${testId}-post-block`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-[11px] uppercase tracking-[0.18em] text-brass">Texte du post {postState === "loading" && "· rédaction IA…"}{postState === "error" && "· IA indisponible, texte simple"}</span>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => fetchPost(true)} disabled={postState === "loading"} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid={`${testId}-post-regen`}><RefreshCw className={`h-3 w-3 ${postState === "loading" ? "animate-spin" : ""}`} /> Reformuler</button>
                <button type="button" onClick={copyPost} disabled={!post} className="text-xs text-slate-200 hover:text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid={`${testId}-post-copy`}>{postCopied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />} {postCopied ? "Copié" : "Copier le texte"}</button>
              </div>
            </div>
            <textarea value={post} onChange={(e) => setPost(e.target.value)} rows={6} placeholder="Rédaction du post en cours…" className="w-full resize-y rounded-lg bg-transparent text-sm text-slate-200 leading-relaxed outline-none placeholder:text-slate-500" data-testid={`${testId}-post-text`} />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <button type="button" onClick={download} className={btn} data-testid={`${testId}-download`}><Download className="h-4 w-4" /> Télécharger</button>
            <button type="button" onClick={copy} disabled={!canCopy} className={`${btn} disabled:opacity-50`} data-testid={`${testId}-copy`}>{copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />} {copied ? "Copiés" : "Copier image + texte"}</button>
            <button type="button" onClick={share} className={btn} data-testid={`${testId}-native`}><Share2 className="h-4 w-4" /> Partager</button>
            <button type="button" onClick={linkedin} className={`${btn} border-brass/50 text-brass hover:bg-brass/10`} data-testid={`${testId}-linkedin`}><Linkedin className="h-4 w-4" /> LinkedIn</button>
          </div>
          <p className="text-xs text-slate-400">Sur LinkedIn : collez (Ctrl+V) dans la zone de rédaction — le texte et l'image sont dans le presse-papiers ; si seul l'un des deux apparaît, utilisez « Copier le texte » puis « Télécharger » pour l'image. Sur mobile, « Partager » envoie image et texte vers WhatsApp, etc.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
